// src/components/ProformaInvoiceDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import ProformaInvoiceDetails from "./ProformaInvoiceDetails";
import ProformaInvoiceItemsEditor from "./ProformaInvoiceItemsEditor";
import { countries } from "../constants/countryList";
import { LazyLoading } from "./ui/LazyLoading";
import toast from "react-hot-toast";
import { toastApiError, toastErrors } from "../utils/toastErrors";
import { PROFORMA_API_ERROR_LABELS, validateProformaInvoice } from "../utils/invoiceValidation";

export default function ProformaInvoiceDetail() {
  const { invoiceNumber } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [buyers, setBuyers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showDelete, setShowDelete] = useState(false);

  // --- Select options (match InvoiceDetail style)
  const currencyOptions = [
    { value: "USD", label: "دلار آمریکا" },
    { value: "EUR", label: "یورو" },
    { value: "GBP", label: "پوند استرلینگ" },
    { value: "IRR", label: "ریال" },
    { value: "AED", label: "درهم امارات" },
  ];
  const meansOfTransportOptions = [
    { value: "By Truck", label: "By Truck" },
    { value: "By AirPlane", label: "By AirPlane" },
    { value: "By Vessel", label: "By Vessel" },
    { value: "By Train", label: "By Train" },
  ];
  const termsOfDeliveryOptions = [
    { value: "CFR", label: "CFR" },
    { value: "CIF", label: "CIF" },
    { value: "CIP", label: "CIP" },
    { value: "CPT", label: "CPT" },
    { value: "DAP", label: "DAP" },
    { value: "DDP", label: "DDP" },
    { value: "DPU", label: "DPU" },
    { value: "EXW", label: "EXW" },
    { value: "FAS", label: "FAS" },
    { value: "FCA", label: "FCA" },
    { value: "FOB", label: "FOB" },
  ];
  const unitOptions = [
    { value: "KG", label: "KG" },
    { value: "M3", label: "M3" },
    { value: "M2", label: "M2" },
    { value: "M", label: "M" },
    { value: "PCS", label: "PCS" },
    { value: "SET", label: "SET" },
  ];
  const termsOfPaymentOptions = [
    { value: "T/T", label: "T/T" },
    { value: "L/C", label: "L/C" },
    { value: "D/P", label: "D/P" },
    { value: "D/A", label: "D/A" },
  ];
  const standardOptions = [
    { value: "JIS", label: "JIS" },
    { value: "ISO", label: "ISO" },
  ];

  const sellerOptions = (sellers || []).map((sel) => ({
    value: sel.id,
    label: sel.seller_name,
  }));

  const buyerOptions = (buyers || []).map((buyer) => ({
    value: buyer.id,
    label: buyer.buyer_name,
  }));

  const countryOptions = (countries || []).map((country) => ({
    value: country.name,
    label: `${country.name} (${country.persianName})`,
  }));

  // --- Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [invoiceRes, buyersRes, sellersRes] = await Promise.all([
          axiosInstance.get(
            `/documents/proforma-invoices/by-number/${invoiceNumber}/`
          ),
          axiosInstance.get("/documents/buyers/"),
          axiosInstance.get("/documents/sellers/"),
        ]);
        setInvoice(invoiceRes.data);
        setBuyers(buyersRes.data);
        setSellers(sellersRes.data);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError("دریافت اطلاعات پروفورما ناموفق بود.");
        toastApiError(err, {}, "دریافت اطلاعات پروفورما ناموفق بود.");
        setLoading(false);
      }
    };
    fetchData();
  }, [invoiceNumber]);

  // --- Field handlers
  const handleInvoiceFieldChange = (field, value) => {
    setInvoice((prev) => ({ ...prev, [field]: value }));
  };
  const handleItemsChange = (updatedItems) => {
    setInvoice((prev) => ({ ...prev, items: updatedItems }));
  };

  // --- Submit / Delete
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!invoice) return;
    const problems = validateProformaInvoice(invoice);
    if (problems.length) {
      toastErrors(problems);
      return;
    }
    try {
      const { data } = await axiosInstance.put(`/documents/proforma-invoices/${invoice.id}/`, {
        ...invoice,
        proforma: invoice.proforma || null,
      });
      setInvoice(data);
      toast.success("تغییرات با موفقیت ذخیره شد.");
    } catch (err) {
      toastApiError(err, PROFORMA_API_ERROR_LABELS, "ذخیره تغییرات پروفورما ناموفق بود.");
    }
  };

  const confirmDelete = async () => {
    if (!invoice) return;
    try {
      await axiosInstance.delete(`/documents/proforma-invoices/${invoice.id}/`);
      toast.success("پروفورما با موفقیت حذف شد.");
      navigate("/proforma-invoices/list");
    } catch (err) {
      toastApiError(err, {}, "حذف پروفورما ناموفق بود.");
    } finally {
      setShowDelete(false);
    }
  };

  if (loading) return <LazyLoading />;
  if (!invoice) return <div className="text-red-600">{error}</div>;

  return (
    <div dir="rtl" className="font-vazir mt-20 md:mt-24 px-5">
      {/* Top bar (matches InvoiceDetail) */}
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">جزئیات پروفورما</h1>
          <p className="text-gray-500 text-sm mt-1">
            شماره پروفورما:{" "}
            <span className="font-semibold">
              {invoice?.proforma_invoice_number}
            </span>
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/proforma-invoices/list"
            className="px-4 py-2 rounded-xl border hover:bg-gray-50 transition"
          >
            بازگشت
          </Link>
          <button
            type="button"
            onClick={() => setShowDelete(true)}
            className="px-4 py-2 rounded-xl border text-red-600 hover:bg-red-50 transition"
          >
            حذف پروفورما
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition"
          >
            ثبت تغییرات
          </button>
        </div>
      </div>

      {/* Details card */}
      <form onSubmit={handleSubmit} noValidate className="space-y-6">
        <div className="rounded-2xl border bg-white shadow-sm">
          <div className="border-b px-4 md:px-6 py-4">
            <h2 className="text-base font-semibold">مشخصات پروفورما</h2>
          </div>
          <div className="px-4  md:px-6 py-6">
            <ProformaInvoiceDetails
              invoice={invoice}
              onFieldChange={handleInvoiceFieldChange}
              sellerOptions={sellerOptions}
              buyerOptions={buyerOptions}
              currencyOptions={currencyOptions}
              termsOfDeliveryOptions={termsOfDeliveryOptions}
              meansOfTransportOptions={meansOfTransportOptions}
              termsOfPaymentOptions={termsOfPaymentOptions}
              standardOptions={standardOptions}
              countryOptions={countryOptions}
            />
          </div>
        </div>

        {/* Items card */}
        <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
          <div className="border-b px-4 md:px-6 py-4">
            <h2 className="text-base font-semibold">اقلام پروفورما</h2>
          </div>
          <div className="px-4 md:px-6 py-6">
            <ProformaInvoiceItemsEditor
              items={invoice.items}
              onItemsChange={handleItemsChange}
              unitOptions={unitOptions}
              countryOptions={countryOptions}
            />
          </div>
        </div>

        {/* Sticky action bar (mobile-friendly) */}
        <div className="sticky bottom-3 mt-6 z-10">
          <div className="mx-auto w-full md:w-auto md:ml-auto flex flex-col md:flex-row gap-2 md:justify-end bg-white/80 backdrop-blur rounded-2xl border p-3 shadow-sm">
            <button
              type="button"
              onClick={() => setShowDelete(true)}
              className="px-4 py-2 rounded-xl border text-red-600 hover:bg-red-50 transition"
            >
              حذف پروفورما
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition"
            >
              ثبت تغییرات
            </button>
          </div>
        </div>
      </form>

      {/* Delete confirmation modal (no window.confirm) */}
      {showDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          dir="rtl"
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold mb-2">حذف پروفورما</h3>
            <p className="text-sm text-gray-600 mb-5">
              آیا از حذف این پروفورما مطمئن هستید؟ این عملیات قابل بازگشت نیست.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDelete(false)}
                className="inline-flex items-center px-4 py-2 rounded-xl border bg-white hover:bg-gray-50"
              >
                انصراف
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="inline-flex items-center px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700"
              >
                تایید حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
